import { GeckoMarkupLayer } from 'types'

interface LayerVisualStateOptions {
  /** Layer ids the user has hidden -- dropped from the output entirely. */
  hiddenLayerIds: ReadonlySet<string>
  /**
   * Layer ids currently hovered in the panel -- recolored to stand out. A
   * single panel row may own multiple layers (an LTA's indication rect + its
   * flaw boundary), so hovering a row highlights all of them.
   */
  hoveredLayerIds: ReadonlySet<string>
  /** Color applied to hovered layers. */
  hoverColor: string
}

/**
 * Returns a copy of the hovered layer recolored to `color`, or the original
 * layer if it can't be cloned. Recoloring goes through the layer's `raw` JSON
 * because `data.color` is only set at construction time. The whole clone --
 * including reading `raw` -- is guarded, since some layers (e.g. coordinate
 * markup) intentionally bypass `CreateFromJSON` and may not carry a usable
 * `raw`.
 */
function recolorLayer(
  layer: GeckoMarkupLayer,
  color: string
): GeckoMarkupLayer {
  try {
    return GeckoMarkupLayer.CreateFromJSON({
      ...layer.raw,
      data: { ...layer.raw.data, color },
    })
  } catch {
    return layer
  }
}

/**
 * Derives the markup layers to hand to the viewer from the ambient layers plus
 * the panel's view state: hidden layers are removed (so their geometry unmounts)
 * and hovered layers are recolored to `hoverColor` so they stand out on the
 * model. Pure -- unit-testable without the viewer.
 */
export function applyLayerVisualState(
  layers: ReadonlyArray<GeckoMarkupLayer>,
  { hiddenLayerIds, hoveredLayerIds, hoverColor }: LayerVisualStateOptions
): GeckoMarkupLayer[] {
  return layers
    .filter((layer) => !hiddenLayerIds.has(layer.id))
    .map((layer) =>
      hoveredLayerIds.has(layer.id) ? recolorLayer(layer, hoverColor) : layer
    )
}
